import { useState, useEffect, useCallback, useMemo } from "react";
import { useAPI } from "/src/hooks/useAPI";

export function useSimulationData(apiBaseUrl, authToken, enabled = true) {
  const [allSimulations, setAllSimulations] = useState([]);
  const [recentSimulationsWithEval, setRecentSimulationsWithEval] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const {callAPI} = useAPI();

  // Fetch recent simulations for a specific scenario (Ad-hoc request)
  const fetchRecentSimulationsForScenario = useCallback(
    async (scenarioId, limit = 5) => {
      if (!enabled || !authToken || !apiBaseUrl) return [];

      try {
        const response = await callAPI(`recent_simulations_with_evaluation?scenario_id=${scenarioId}&limit=${limit}`,
          {
            token: authToken ,
          }
        );
        return response;
      } catch (error) {
        console.error("Error fetching recent simulations for scenario:", error);
        return [];
      }
    },
    [enabled, authToken, apiBaseUrl]
  );

  // Main refresh function (updates global simulation lists)
  const refreshSimulations = useCallback(async () => {
    if (!enabled || !authToken || !apiBaseUrl) return;
    
    setLoading(true);
    try {
      const [simulationsData, recentData] = await Promise.all([
        callAPI(`optimized_get_simulations`, { token: authToken }),
        callAPI(`recent_simulations_with_evaluation?limit=5`, { token: authToken }),
      ]);

      setAllSimulations(simulationsData);
      setRecentSimulationsWithEval(recentData);
      setLastFetch(Date.now());
    } catch (error) {
      console.error("Error refreshing simulations:", error);
    } finally {
      setLoading(false);
    }
  }, [enabled, authToken, apiBaseUrl]);

  // Trigger initial fetch
  useEffect(() => {
    if (!enabled) return;
    refreshSimulations();
  }, [enabled, refreshSimulations]);

  // Compute derived state efficiently
  const simulationsData = useMemo(() => {
    const completed = allSimulations.filter(
      (sim) => sim.status === "completed" && sim.evaluation !== null
    );

    const recentCompleted = [...completed]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);

    const inProgress = allSimulations.filter(
      (sim) => sim.status !== "completed"
    );

    return {
      all: allSimulations,
      completed,
      recentCompleted,
      inProgress,
      loading: loading,
      lastFetch: lastFetch,
      recentWithEvaluation: recentSimulationsWithEval,
      recentEvaluationLoading: loading,
    };
  }, [allSimulations, recentSimulationsWithEval, loading, lastFetch]);

  return {
    simulationsData,
    refreshSimulations,
    fetchRecentSimulationsForScenario,
  };
}